import styled from '@emotion/styled';
import { RacemapColors } from '@racemap/utilities/consts/common';
import { formatAsKM, formatAsMeters } from '@racemap/utilities/formattings/units';
import { getRelativeTime } from '@racemap/utilities/functions/datetime';
import { PlacingToShadowtrack } from '@racemap/utilities/types/prediction';
import type { Reader } from '@racemap/utilities/types/types';
import { Table, Tag, Typography } from 'antd';
import { type FC, type ReactElement, useState } from 'react';
import { IconArrowDown, IconArrowUp } from '../../BasicComponents/Icons';
import { TransponderId } from '../TransponderId';

const { Text, Title } = Typography;

export interface Props {
  reader: Reader;
  index: number;
  onEnter: () => void;
  onLeave: () => void;
  onReaderClick?: (reader: Reader) => void;
  focused?: boolean;
}

export const ReadersListEntry: FC<Props> = ({
  reader,
  index,
  onEnter,
  onLeave,
  onReaderClick,
  focused = false,
}) => {
  const lastRead = reader.lastRead;
  const isFarAway = reader.placing === PlacingToShadowtrack.FarAway;
  const hasNoShadowtrack = reader.placing === PlacingToShadowtrack.NoShadowTrack;
  const subReaderIds = reader.subReaderIds;

  return (
    <Container>
      {hasNoShadowtrack && (
        <DisabledOverlay>
          <Text strong>No Shadow Track configured</Text>
        </DisabledOverlay>
      )}
      {isFarAway && (
        <DisabledOverlay>
          <Text strong>Away from Raceground</Text>
        </DisabledOverlay>
      )}
      <Entry
        onClick={() => onReaderClick?.(reader)}
        onMouseEnter={onEnter}
        onMouseLeave={onLeave}
        disabled={isFarAway || hasNoShadowtrack}
        focused={focused}
      >
        <Header>
          <Title title={reader.readerId} level={5}>
            {index}
            {' - '}
            {reader.name ? (
              <span>
                {reader.name}
                <Tiny>&nbsp;{reader.readerId}</Tiny>
              </span>
            ) : (
              reader.readerId
            )}
          </Title>
          <Badges>
            {reader.placing === PlacingToShadowtrack.Mapped && (
              <Tag color="success">Within range</Tag>
            )}
            {reader.placing === PlacingToShadowtrack.Near && <Tag color="error">Out of range</Tag>}
            {(lastRead?.transponderId != null || !!reader.readsCount) && (
              <Tag color="success">Got Reads</Tag>
            )}
            {reader.placing === PlacingToShadowtrack.OnTrack && <Tag color="success">Split</Tag>}
          </Badges>
        </Header>
        <Infos focused={focused}>
          <LabeledValue label="Reads" value={reader.readsCount || 0} />
          {lastRead != null && reader.receivedAt && (
            <LabeledValue label="Last Read" value={getRelativeTime(reader.receivedAt)} />
          )}
          {lastRead?.transponderId != null && (
            <Value>
              from <TransponderId id={lastRead.transponderId} />
            </Value>
          )}
          <LabeledValue
            label="Mappings"
            value={reader.mappings?.length || '--'}
            expansion={
              reader.mappings?.length ? <MappingTable mappings={reader.mappings} /> : undefined
            }
          />
          {subReaderIds != null && subReaderIds.length > 0 && (
            <LabeledValue label="Reader Ids" value={subReaderIds.join(' / ')} />
          )}
        </Infos>
      </Entry>
    </Container>
  );
};

interface LabeledValueProps {
  label: string;
  value: string | number;
  expansion?: ReactElement<HTMLDivElement>;
}

const LabeledValue: FC<LabeledValueProps> = ({ label, value, expansion }) => {
  const [expanded, setExpanded] = useState(false);

  const expansionTrigger = expanded ? (
    <IconArrowUp onClick={() => setExpanded(false)} />
  ) : (
    <IconArrowDown onClick={() => setExpanded(true)} />
  );

  return (
    <>
      <Label className="label">
        {label}
        {label !== '' ? ':' : ''}
      </Label>
      <Value clickable={expansion != null} title={value.toString()}>
        {expansion != null && expansionTrigger} {value}
      </Value>
      {expanded && expansion}
    </>
  );
};

interface MappingTableProps {
  mappings: Reader['mappings'];
}

const mappingColumns = [
  { title: '#', dataIndex: 'index', key: 'index' },
  { title: 'Offset', dataIndex: 'offset', key: 'offset' },
  { title: 'Distance to track', dataIndex: 'distance', key: 'distance' },
];

const MappingTable: FC<MappingTableProps> = ({ mappings }) => {
  if (mappings == null || mappings.length < 1) return <></>;

  const data = mappings.map((m, i) => ({
    key: m.offset,
    index: i + 1,
    offset: formatAsKM({ meters: m.offset }),
    distance: formatAsMeters({ meters: m.distance }),
  }));

  return (
    <MappingContainer>
      <Table bordered columns={mappingColumns} dataSource={data} pagination={false} size="small" />
    </MappingContainer>
  );
};

const Tiny = styled.span`
  font-size: 0.6em;
`;

const Container = styled.div`
  border-bottom: solid 1px ${RacemapColors.LightGray};
  position: relative;
`;

const MappingContainer = styled.div`
  grid-column-start: 1;
  grid-column-end: 3;
  margin-top: 10px;
`;

const Label = styled.div`
  grid-column: 1;
`;

const Value = styled.div<{ clickable?: boolean }>`
  grid-column: 2;
  justify-self: end;
  cursor: ${(props) => (props.clickable ? 'pointer' : 'default')};
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
`;

type EntryProps = {
  disabled?: boolean;
  focused?: boolean;
};

const Entry = styled.div<EntryProps>`
  cursor: pointer;
  padding: 10px 12px;
  opacity: ${(props) => (props.disabled ? 0.3 : 1)};
  background: ${(props) => (props.focused ? RacemapColors.CloudBlue : 'transparent')};
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 8px;
`;

interface InfosProps {
  focused?: boolean;
}

const Infos = styled.div<InfosProps>`
  display: grid;
  font-size: 0.85em;
  grid-template-columns: auto 1fr;
  gap: 3px 12px;
  margin-top: 8px;
  .label {
    color: ${(props) => (props.focused ? RacemapColors.DarkDarkGray : RacemapColors.DarkGray)};
    white-space: nowrap;
  }
`;

const Badges = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: flex-end;
  flex-shrink: 0;
`;

const DisabledOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${RacemapColors.LightLightGray}80;
  z-index: 1;
  pointer-events: none;
`;
